import type { DensitySnapshot } from "@screener/protocol";
import { exchangeLabel, isMarketKind, marketLabel } from "@/lib/labels";
import { isTerminalGroup } from "./terminal-groups";
import {
  MAX_BINDING,
  MAX_PORT,
  type MetascalpMapping,
  MIN_BINDING,
  MIN_PORT,
  type TerminalKind,
  type TerminalMapping,
  type TerminalSettings,
  type TerminalSettingsStore,
} from "./terminal-settings";

/**
 * Черновик листа настроек терминала (§10 1.36): строки полей, пока трейдер
 * правит. В настройки он превращается только целиком и только годным.
 */

export interface MappingDraftRow {
  /** `"<exchange>:<market>"` — тот же ключ, что у `TerminalSettings.mapping`. */
  key: string;
  e: string;
  m: string;
}

export interface MetascalpDraftRow {
  /** `"<exchange>:<market>"` — тот же ключ, что у `MetascalpSettings.mapping`. */
  key: string;
  /** Строки, пока трейдер печатает: «1a» видно как есть. Пустые обе — соответствия нет. */
  exchange: string;
  market: string;
}

export interface TerminalSettingsDraft {
  terminal: TerminalKind;
  /** Порт MetaScalp; пусто — автопоиск (§89 п. 3). */
  metascalpPort: string;
  /** Связки MetaScalp строкой: «1, 2, 3». Разбор — `parseBindingsInput`. */
  bindings: string;
  metascalpRows: MetascalpDraftRow[];
  enabled: boolean;
  host: string;
  /** Набор групп строкой, как его называет трейдер: «1, 2, 3». Разбор — `parseGroupsInput`. */
  groups: string;
  /** Строка, пока трейдер печатает: «12a» должна быть видна как есть, а не молча округлена. */
  port: string;
  rows: MappingDraftRow[];
}

export interface TerminalDraftErrors {
  host?: string;
  port?: string;
  groups?: string;
  metascalpPort?: string;
  bindings?: string;
  /** Первая найденная ошибка кода, с названием рынка. */
  codes?: string;
  /** Ключи строк с неверным кодом — их поля получают `aria-invalid`. */
  codeRows?: string[];
}

export interface LiveMarket {
  exchange: string;
  market: string;
}

/** Биржи без поддержки в MetaScalp (§89 п. 4): в таблице кодов их строк нет. */
export const METASCALP_UNSUPPORTED_EXCHANGES: readonly string[] = ["coinbase", "kraken"];

export const PORT_ERROR_TEXT = `Порт — целое число от ${MIN_PORT} до ${MAX_PORT}`;
export const HOST_ERROR_TEXT = "Адрес пуст или не годится для ws://-адреса";
export const GROUPS_EMPTY_ERROR_TEXT = "Выберите хотя бы одну группу";

export function bindingRepeatedText(binding: string): string {
  return `Связка ${binding} повторяется`;
}

export function bindingRangeText(binding: string): string {
  return `Связка ${binding} — целое число от ${MIN_BINDING} до ${MAX_BINDING}`;
}

export function codeErrorText(marketName: string, field: "exchange" | "market"): string {
  return `${marketName}: ${field} — целое число от 0`;
}

/** Подпись рынка по ключу `"<exchange>:<market>"`, как её видит трейдер. */
export function marketRowLabel(key: string): string {
  const [exchange = "", market = ""] = key.split(":");
  return `${exchangeLabel(exchange)} · ${isMarketKind(market) ? marketLabel(market) : market}`;
}

export interface ParsedBindings {
  bindings: number[];
  /** Первая найденная ошибка — с названием номера; `null` — набор годен (возможно, пуст). */
  error: string | null;
}

/**
 * Набор связок из строки ввода: разделители — запятая и пробелы, порядок
 * сохраняется. Номер вне 1–500, не целое или повтор — ошибка с этим номером.
 */
export function parseBindingsInput(input: string): ParsedBindings {
  const bindings: number[] = [];
  for (const token of input.split(/[\s,]+/)) {
    if (token === "") {
      continue;
    }
    const value = /^\d+$/.test(token) ? Number(token) : Number.NaN;
    if (!Number.isInteger(value) || value < MIN_BINDING || value > MAX_BINDING) {
      return { bindings, error: bindingRangeText(token) };
    }
    if (bindings.includes(value)) {
      return { bindings, error: bindingRepeatedText(token) };
    }
    bindings.push(value);
  }
  return { bindings, error: null };
}

function parseCode(input: string): number | null {
  return /^\d+$/.test(input.trim()) ? Number(input.trim()) : null;
}

export function groupRepeatedText(group: string): string {
  return `Группа ${group} повторяется`;
}

export function groupUnknownText(group: string): string {
  return `TigerTrade не знает группу ${group}`;
}

export interface ParsedGroups {
  groups: string[];
  /** Первая найденная ошибка — с названием группы; `null` — набор годен (возможно, пуст). */
  error: string | null;
}

/**
 * Набор групп из строки ввода: разделители — запятая и/или пробелы, буквы
 * приводятся к верхнему регистру, порядок сохраняется. Ошибки (повтор,
 * неизвестная группа) называют группу; при ошибке `groups` содержит
 * разобранное до неё.
 */
export function parseGroupsInput(input: string): ParsedGroups {
  const groups: string[] = [];
  for (const token of input.split(/[\s,]+/)) {
    if (token === "") {
      continue;
    }
    const group = token.toUpperCase();
    if (!isTerminalGroup(group)) {
      return { groups, error: groupUnknownText(group) };
    }
    if (groups.includes(group)) {
      return { groups, error: groupRepeatedText(group) };
    }
    groups.push(group);
  }
  return { groups, error: null };
}

/** Уникальные биржа × рынок живых плотностей, в порядке первого появления. */
export function liveMarketsOf(rows: readonly DensitySnapshot[]): LiveMarket[] {
  const seen = new Set<string>();
  const result: LiveMarket[] = [];
  for (const row of rows) {
    const { exchange, market } = row.instrument;
    const key = `${exchange}:${market}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ exchange, market });
    }
  }
  return result;
}

/** Рынки потока и сохранённые ключи вне потока, без повторов; строки отсортированы по ключу. */
export function draftFromSettings(
  settings: TerminalSettings,
  liveMarkets: readonly LiveMarket[],
): TerminalSettingsDraft {
  const keys: string[] = [];
  for (const { exchange, market } of liveMarkets) {
    const key = `${exchange}:${market}`;
    if (!keys.includes(key)) {
      keys.push(key);
    }
  }
  for (const key of Object.keys(settings.mapping)) {
    if (!keys.includes(key)) {
      keys.push(key);
    }
  }
  const sorted = keys.sort();
  // Coinbase и Kraken MetaScalp не поддерживает: их рынков в его таблице нет.
  const metascalpKeys = [...new Set([...keys, ...Object.keys(settings.metascalp.mapping)])]
    .filter((key) => !METASCALP_UNSUPPORTED_EXCHANGES.includes(key.split(":")[0] ?? ""))
    .sort();
  return {
    terminal: settings.terminal,
    metascalpPort: settings.metascalp.port === null ? "" : String(settings.metascalp.port),
    bindings: settings.metascalp.bindings.join(", "),
    metascalpRows: metascalpKeys.map((key) => {
      const mapping: MetascalpMapping | undefined = settings.metascalp.mapping[key];
      return {
        key,
        exchange: mapping === undefined ? "" : String(mapping.exchange),
        market: mapping === undefined ? "" : String(mapping.market),
      };
    }),
    enabled: settings.enabled,
    host: settings.host,
    groups: settings.groups.join(", "),
    port: String(settings.port),
    rows: sorted.map((key) => {
      const mapping: TerminalMapping | undefined = settings.mapping[key];
      return { key, e: mapping?.e ?? "", m: mapping?.m ?? "" };
    }),
  };
}

/** Черновики совпадают по всем полям, включая строки таблиц соответствия (порядок строк значим). */
export function draftsEqual(a: TerminalSettingsDraft, b: TerminalSettingsDraft): boolean {
  return (
    a.terminal === b.terminal &&
    a.metascalpPort === b.metascalpPort &&
    a.bindings === b.bindings &&
    a.enabled === b.enabled &&
    a.host === b.host &&
    a.groups === b.groups &&
    a.port === b.port &&
    a.rows.length === b.rows.length &&
    a.rows.every((row, index) => {
      const other = b.rows[index];
      return other !== undefined && row.key === other.key && row.e === other.e && row.m === other.m;
    }) &&
    a.metascalpRows.length === b.metascalpRows.length &&
    a.metascalpRows.every((row, index) => {
      const other = b.metascalpRows[index];
      return (
        other !== undefined &&
        row.key === other.key &&
        row.exchange === other.exchange &&
        row.market === other.market
      );
    })
  );
}

/**
 * Черновик на момент открытия листа: рынки потока спрашиваются у источника
 * сейчас, а не берутся из значения, которое экран держал бы живым (design.md
 * stage9-render-scope, решение 4). Пока лист открыт, поток черновик не трогает.
 */
export function draftForOpening(
  settings: TerminalSettings,
  getLiveMarkets: () => readonly LiveMarket[],
): TerminalSettingsDraft {
  return draftFromSettings(settings, getLiveMarkets());
}

function parsePort(port: string): number | null {
  if (!/^\d+$/.test(port.trim())) {
    return null;
  }
  const value = Number(port.trim());
  return value >= MIN_PORT && value <= MAX_PORT ? value : null;
}

/**
 * Из адреса собирается именно адрес сервера: `new WebSocket` бросает на
 * `bad host` и `::1` без скобок, а `a/b` или `host?x` собрались бы в URL с
 * путём и запросом вместо порта. Поэтому мало того, что URL разобрался, —
 * в нём не должно быть ни пути, ни запроса, ни учётных данных.
 */
function hostMakesUrl(host: string, port: number): boolean {
  const trimmed = host.trim();
  if (trimmed === "") {
    return false;
  }
  try {
    const url = new URL(`ws://${trimmed}:${port}`);
    return (
      url.hostname !== "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === "" &&
      url.username === "" &&
      url.password === ""
    );
  } catch {
    return false;
  }
}

/**
 * Ошибки кодов в таблице MetaScalp: первое сообщение с названием рынка и
 * ключи всех неверных строк (для подсветки полей). Пустая пара — «нет
 * соответствия», не ошибка.
 */
function codeErrors(
  rows: readonly MetascalpDraftRow[],
): { message: string; keys: string[] } | null {
  let message: string | null = null;
  const keys: string[] = [];
  for (const row of rows) {
    if (row.exchange.trim() === "" && row.market.trim() === "") {
      continue;
    }
    const label = marketRowLabel(row.key);
    const bad =
      parseCode(row.exchange) === null
        ? codeErrorText(label, "exchange")
        : parseCode(row.market) === null
          ? codeErrorText(label, "market")
          : null;
    if (bad !== null) {
      message ??= bad;
      keys.push(row.key);
    }
  }
  return message === null ? null : { message, keys };
}

/** Порт MetaScalp: пусто — автопоиск (`null`), иначе число; `undefined` — не число. */
function parseMetascalpPort(input: string): number | null | undefined {
  if (input.trim() === "") {
    return null;
  }
  return parsePort(input) ?? undefined;
}

function validateMetascalp(draft: TerminalSettingsDraft): TerminalDraftErrors {
  const errors: TerminalDraftErrors = {};
  if (parseMetascalpPort(draft.metascalpPort) === undefined) {
    errors.metascalpPort = PORT_ERROR_TEXT;
  }
  const bindings = parseBindingsInput(draft.bindings);
  if (bindings.error !== null) {
    errors.bindings = bindings.error;
  }
  const codes = codeErrors(draft.metascalpRows);
  if (codes !== null) {
    errors.codes = codes.message;
    errors.codeRows = codes.keys;
  }
  return errors;
}

/** Проверяется только блок выбранного терминала: скрытые поля ошибок не дают. */
export function validateDraft(draft: TerminalSettingsDraft): TerminalDraftErrors {
  return draft.terminal === "metascalp" ? validateMetascalp(draft) : validateTigerTrade(draft);
}

function validateTigerTrade(draft: TerminalSettingsDraft): TerminalDraftErrors {
  const errors: TerminalDraftErrors = {};
  const port = parsePort(draft.port);
  if (!hostMakesUrl(draft.host, port ?? MIN_PORT)) {
    errors.host = HOST_ERROR_TEXT;
  }
  if (port === null) {
    errors.port = PORT_ERROR_TEXT;
  }
  const parsed = parseGroupsInput(draft.groups);
  if (parsed.error !== null) {
    errors.groups = parsed.error;
  } else if (draft.enabled && parsed.groups.length === 0) {
    errors.groups = GROUPS_EMPTY_ERROR_TEXT;
  }
  return errors;
}

/**
 * Настройки из черновика; `null` — черновик неверен, сохранять нечего.
 * Пустые `e` или `m` означают «нет соответствия» и удаляют ключ. Блок
 * невыбранного терминала не проверялся и не правился на виду: он остаётся
 * прежним, как в `previous`, а не разобранным до ошибки.
 */
export function settingsFromDraft(
  draft: TerminalSettingsDraft,
  previous: TerminalSettings,
): TerminalSettings | null {
  if (Object.keys(validateDraft(draft)).length > 0) {
    return null;
  }
  const common = { terminal: draft.terminal, enabled: draft.enabled };
  if (draft.terminal === "metascalp") {
    const mapping: Record<string, MetascalpMapping> = {};
    for (const row of draft.metascalpRows) {
      const exchange = parseCode(row.exchange);
      const market = parseCode(row.market);
      if (exchange !== null && market !== null) {
        mapping[row.key] = { exchange, market };
      }
    }
    return {
      ...previous,
      ...common,
      metascalp: {
        port: parseMetascalpPort(draft.metascalpPort) ?? null,
        mapping,
        bindings: parseBindingsInput(draft.bindings).bindings,
      },
    };
  }
  const port = parsePort(draft.port);
  if (port === null) {
    return null;
  }
  const mapping: Record<string, TerminalMapping> = {};
  for (const row of draft.rows) {
    const e = row.e.trim();
    const m = row.m.trim();
    if (e !== "" && m !== "") {
      mapping[row.key] = { e, m };
    }
  }
  return {
    ...previous,
    ...common,
    host: draft.host.trim(),
    port,
    mapping,
    groups: parseGroupsInput(draft.groups).groups,
  };
}

/**
 * Сохранение: только в хранилище настроек браузера. Ни поток, ни запросный
 * канал сюда не подключены — терминал свойство машины трейдера (D2).
 */
export function saveTerminalDraft(
  draft: TerminalSettingsDraft,
  store: Pick<TerminalSettingsStore, "get" | "set">,
): boolean {
  const settings = settingsFromDraft(draft, store.get());
  if (settings === null) {
    return false;
  }
  store.set(settings);
  return true;
}
